import Image from "next/image";
import { Box, BoxProps, Divider, Stack, StackProps, Typography, TypographyProps, useMediaQuery } from "@mui/material";
import AppIconButton, { AppIconButtonProps } from "components/v2/atoms/app-icon-button/AppIconButton";
import { API_URL } from "helpers/common";
import { PageProps } from "pages/_app";
import loginScreenTable from "assets/images/opentax/login_screen_table.png";
import loginVector from "assets/images/opentax/vector.png";
import router from "next/router";
import AppSocialButton, { AppSocialButtonProps } from "components/v2/atoms/app-social-button/AppSocialButton";
import AppLink from "components/v2/atoms/app-link/AppLink";
import AppButton, { AppButtonProps } from "components/v2/atoms/app-button/AppButton";
import freeagentBtn from "assets/icons/landing-page/FreeAgent_white.png";
import { usePartnerId } from "hooks/partner-id";

interface RightPanelProps {
    partner_name: string;
    isMobile: boolean;
}

interface LeftPanelProps {
    isMobile: boolean;
}

function OpenTax(_: PageProps) {
    const { partner } = usePartnerId({ defaultPartner: "freeagent" });

    return <Content partner={partner} />;
}

function Content({ partner }: { partner: string }) {
    const isMobile = useMediaQuery("(max-width:899px)", {
        defaultMatches: true,
    });

    const outerContainerProps: BoxProps = {
        sx: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: isMobile ? 0 : "20px",
            boxSizing: isMobile ? "auto" : "border-box",
            minHeight: isMobile ? "100%" : "80vh",
            height: "auto",
            maxHeight: "auto",
        },
    };

    const containerProps: BoxProps = {
        sx: {
            width: "100%",
            height: "auto",
            maxHeight: "auto",
            maxWidth: isMobile ? "100%" : "950px",
            margin: "0 auto",
            borderRadius: isMobile ? 0 : "15px",
            backgroundColor: "#fff",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
        },
    };

    const stackProps: StackProps = {
        sx: {
            flexDirection: { xs: "column", md: "row" },
        },
    };

    const leftStackProps: StackProps = {
        sx: {
            p: 2,
            pt: { xs: 5, md: 12 },
            pb: { xs: 0, md: 0 },
            flex: {
                xs: 1.5,
                md: 7,
            },
            backgroundColor: "#F8FAFB",
        },
    };

    const rightStackProps: StackProps = {
        sx: {
            p: { xs: 4, md: 3 },
            pt: { xs: 3, md: 4 },
            flex: 4,
            alignItems: "center",
            justifyContent: "center",
        },
    };

    const helpStackProps: TypographyProps = {
        sx: {
            pt: 2,
            pr: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            gap: 0,
        },
    };

    const helpHeadingProps: TypographyProps = {
        variant: "h4",
        color: "info.main",
        fontWeight: 500,
        sx: {
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            gap: 0,
            cursor: "pointer",
            color: "primary.main",
            "&:hover": {
                textDecoration: "underline",
            },
        },
        onClick: () => {
            router.push(`/v2/open-tax/contact-us`);
        },
    };

    const hintButtonProps: AppIconButtonProps = {
        sx: {
            color: "#8655FF",
            fontSize: 14,
        },
        onClick: () => {
            router.push(`/v2/open-tax/contact-us`);
        },
    };

    const rightPanelProps: RightPanelProps = {
        partner_name: partner,
        isMobile: isMobile,
    };

    const leftPanelProps: LeftPanelProps = {
        isMobile: isMobile,
    };

    return (
        <Box {...outerContainerProps}>
            <Box {...containerProps}>
                <Stack {...stackProps}>
                    <Stack {...leftStackProps}>
                        <LeftPanel {...leftPanelProps} />
                    </Stack>
                    {isMobile ? (
                        <Typography {...helpStackProps}>
                            <AppIconButton {...hintButtonProps} />
                            <Typography {...helpHeadingProps}> Help </Typography>
                        </Typography>
                    ) : null}
                    <Stack {...rightStackProps}>
                        <RightPanel {...rightPanelProps} />
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
}

export default OpenTax;

function LeftPanel(_: LeftPanelProps) {
    const stackProps: StackProps = {
        sx: {
            height: "100%",
        },
    };

    const desktopStackProps: StackProps = {
        mt: { xs: 0, md: 11 },
    };

    const containerProps: StackProps = {
        bgcolor: {
            xs: "none",
        },
        pb: { xs: 3, md: 5 },
        borderRadius: "15px",
        mt: { xs: 0, md: -10 },
        marginLeft: { xs: 0, md: 2 },
        marginRight: { xs: 0, md: 2 },
    };

    const logoImageProps: TypographyProps = {
        gap: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    };

    const titleProps: TypographyProps = {
        variant: "h6",
        fontWeight: "500",
        sx: {
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        },
    };

    const headingProps: TypographyProps = {
        variant: "body1",
        fontWeight: "500",
        mt: 1,
        sx: {
            textAlign: "center",
        },
    };

    const subheadingProps: TypographyProps = {
        variant: "body1",
        fontWeight: "500",
        mt: 1,
        sx: {
            color: "text.disabled",
            textAlign: "center",
        },
    };

    const mainImageStackProps: StackProps = {
        sx: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: { xs: 2, md: 3 },
            mb: 2,
        },
    };

    return (
        <Stack {...stackProps}>
            <Stack {...containerProps}>
                <Stack {...desktopStackProps}>
                    <Box
                        sx={{
                            position: "center",
                        }}>
                        <Typography {...logoImageProps}>
                            <Image
                                src={loginVector}
                                width={44}
                                height={39}
                                alt="Login screen"
                                style={{
                                    maxWidth: "100%",
                                    height: "auto",
                                }}
                            />
                            <Typography {...titleProps}>OpenTax</Typography>
                        </Typography>
                        <Typography {...subheadingProps}>Reduce dependency on HMRC helplines</Typography>
                        <Typography {...headingProps}>Data insights beyond your agent services account</Typography>
                    </Box>
                </Stack>
                <Stack {...mainImageStackProps}>
                    <Box
                        sx={{
                            position: "center",
                            width: { xs: "350px", md: "540px" },
                            height: { xs: "81px", md: "206px" },
                        }}>
                        <Image
                            src={loginScreenTable}
                            alt="Login screen"
                            style={{
                                maxWidth: "100%",
                                height: "auto",
                            }}
                        />
                    </Box>
                </Stack>
            </Stack>
        </Stack>
    );
}

function RightPanel(props: RightPanelProps) {
    const isMobile = props.isMobile;

    const stackProps: StackProps = {
        sx: {
            gap: 0,
            height: "100%",
            width: isMobile ? "100%" : "auto",
            maxWidth: isMobile ? "450px" : "auto",
            justifyContent: "space-between",
        },
    };

    const topStackProps: StackProps = {
        sx: {
            gap: {
                xs: 2,
                md: 3,
            },
        },
    };

    const hintButtonProps: AppIconButtonProps = {
        sx: {
            color: "#8655FF",
        },
        onClick: () => {
            router.push(`/v2/open-tax/contact-us`);
        },
    };

    const helpStackProps: TypographyProps = {
        sx: {
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            gap: 0,
            mb: { md: 4 },
        },
    };

    const helpHeadingProps: TypographyProps = {
        fontSize: { xs: 14, md: 16 },
        color: "info.main",
        fontWeight: 500,
        sx: {
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            gap: 0,
            cursor: "pointer",
            color: "primary.main",
            "&:hover": {
                textDecoration: "underline",
            },
        },
        onClick: () => {
            router.push(`/v2/open-tax/contact-us`);
        },
    };

    const googleButtonProps: AppSocialButtonProps = {
        id: "open-tax_steps_email_google",
        brand: "google",
        children: "Sign up with Google",
        onClick() {
            let url = API_URL + "/google_signup";
            url += `?partner_signup=accountant`;
            location.href = url;
        },
    };

    const microsoftButtonProps: AppSocialButtonProps = {
        id: "open-tax_steps_email_microsoft",
        brand: "microsoft",
        children: "Sign up with Microsoft",
        onClick() {
            let url = API_URL + "/microsoft_signup";
            url += `?partner_signup=accountant`;
            location.href = url;
        },
    };

    const emailButtonProps: AppSocialButtonProps = {
        id: "open-tax_steps_email_email",
        brand: "email",
        onClick() {
            let url = API_URL + "/portal/v2/open-tax/steps/email";
            url += `?company_id=accountant`;
            location.href = url;
        },
        children: "Continue with email",
    };

    const freeagentButtonProps: AppButtonProps = {
        variant: "contained",
        sx: {
            color: "white",
            backgroundColor: "#008CFF",
            fontWeight: 600,
            "&:hover": {
                backgroundColor: "#008CFF",
                borderColor: "#008CFF",
            },
            width: { xs: "100%", md: "auto" },
            gap: 0.5,
            alignContent: "center",
            alignItems: "center",
            justifyContent: "center",
        },
        children: (
            <>
                <>Sign up with</>{" "}
                <Image
                    src={freeagentBtn}
                    alt="freeagentBtn"
                    width={80}
                    height={19.71}
                    priority
                    style={{
                        maxWidth: "100%",
                        height: "auto",
                    }}
                />
            </>
        ),
        onClick: () => {
            location.href = API_URL + "/freeagent_accountant_signup";
        },
    };

    const innerStackProps: StackProps = {
        sx: {
            gap: 2,
        },
    };

    const loginLinkProps: TypographyProps = {
        variant: "body1",
        color: "#9DA4AF",
    };

    const buttonStackProps: StackProps = {
        sx: {
            pt: { xs: 3, md: 11 },
            pb: { xs: 2, md: 4 },
            gap: 1,
            textAlign: "center",
        },
    };

    return (
        <Stack {...stackProps}>
            {isMobile ? null : (
                <Typography {...helpStackProps}>
                    <AppIconButton {...hintButtonProps} />
                    <Typography {...helpHeadingProps}> Help </Typography>
                </Typography>
            )}
            <Stack {...topStackProps}>
                <AppButton {...freeagentButtonProps} />

                <Divider>
                    <Typography color="text.secondary">or</Typography>
                </Divider>
                <Stack {...innerStackProps}>
                    <AppSocialButton {...emailButtonProps} />
                    <AppSocialButton {...googleButtonProps} />
                    <AppSocialButton {...microsoftButtonProps} />
                </Stack>
                <Stack {...buttonStackProps}>
                    <Typography {...loginLinkProps}>
                        Already have an account?{" "}
                        <AppLink id="open-tax_steps_phone_tnc" href={API_URL + "/portal/v2/auth/login"}>
                            Log in
                        </AppLink>
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    );
}
